import { useRef, useState } from "react";
import { Download, GitCompareArrows, Plus } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  Field,
  SegmentedControl,
  Select,
} from "../../design-system";
import { RefineMarkdown } from "./manuscript-review";
import { guidelineFixture, type Guideline } from "./refine-data";
import "./refine.css";
export type GuidelineActions = {
  create?: (
    input: Pick<Guideline, "kind" | "label" | "body" | "note">,
  ) => Promise<void>;
  activate?: (id: string) => Promise<void>;
  reload?: () => void;
};
export function GuidelineManager({
  versions,
  actions = {},
  canManage = false,
  loading = false,
  error,
  initialDialog,
}: {
  versions: Guideline[];
  actions?: GuidelineActions;
  canManage?: boolean;
  loading?: boolean;
  error?: string;
  initialDialog?: "create" | "activate" | "diff";
}) {
  const [kind, setKind] = useState("refine"),
    [selected, setSelected] = useState(""),
    [other, setOther] = useState(""),
    [modal, setModal] = useState(initialDialog),
    [label, setLabel] = useState(""),
    [body, setBody] = useState(""),
    [note, setNote] = useState(""),
    [busy, setBusy] = useState(false),
    [failure, setFailure] = useState("");
  const pending = useRef(false);
  const rows = versions.filter((v) => v.kind === kind),
    current = rows.find((v) => v.id === selected) ?? rows[0],
    compare = rows.find((v) => v.id === other) ?? rows[1] ?? current;
  async function submit() {
    if (pending.current || !canManage || (modal !== "create" && !current))
      return;
    if (modal === "create" && (!label.trim() || !body.trim())) {
      setFailure("라벨과 가이드라인 본문을 입력해 주세요.");
      return;
    }
    pending.current = true;
    setBusy(true);
    setFailure("");
    try {
      if (modal === "create") {
        if (!actions.create) throw Error("새 버전 등록이 연결되지 않았습니다.");
        await actions.create({
          kind: kind as Guideline["kind"],
          label,
          body,
          note,
        });
        setLabel("");
        setBody("");
        setNote("");
      } else {
        if (!actions.activate) throw Error("활성 전환이 연결되지 않았습니다.");
        await actions.activate(current!.id);
      }
      setModal(undefined);
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  function download() {
    if (!current) return;
    const url = URL.createObjectURL(
      new Blob([current.body], { type: "text/markdown;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `${current.kind}-guideline-v${current.version}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <div className="mrf-page" data-guidelines>
      <div className="mrf-toolbar">
        <SegmentedControl
          label="가이드라인 종류"
          value={kind}
          onChange={(v) => {
            setKind(v);
            setSelected("");
            setOther("");
          }}
          options={[
            { value: "refine", label: "정형화 (변환)" },
            { value: "generate", label: "생성" },
          ]}
        />
        {!canManage && <Badge>읽기 전용</Badge>}
      </div>
      <p className="mrf-muted">
        버전은 수정하지 않고 새로 남깁니다. 진행 중인 시도는 시작할 때 선택한
        버전을 유지합니다.
      </p>
      {loading ? (
        <div role="status" className="mrf-state">
          가이드라인을 불러오고 있습니다…
        </div>
      ) : error ? (
        <div className="mrf-state" role="alert">
          {error}
          <Button onClick={actions.reload} disabled={!actions.reload}>
            다시 읽기
          </Button>
        </div>
      ) : !current ? (
        <div className="mrf-state">등록된 가이드라인이 없습니다.</div>
      ) : (
        <div className="mrf-guidelines">
          <aside aria-label="가이드라인 버전">
            {rows.map((v) => (
              <button
                key={v.id}
                aria-pressed={current.id === v.id}
                onClick={() => setSelected(v.id)}
              >
                <div>
                  <strong>
                    v{v.version} · {v.label}
                  </strong>
                  <Badge tone={v.active ? "teal" : "neutral"}>
                    {v.active ? "활성" : "비활성"}
                  </Badge>
                </div>
                <p>{v.note}</p>
                <small>
                  {v.author} · {v.createdAt}
                  <br />
                  사용된 시도 {v.uses}회
                </small>
              </button>
            ))}
          </aside>
          <section className="mrf-paper">
            <header>
              <strong>가이드라인 v{current.version}</strong>
              <div className="mrf-inline">
                <Button
                  variant="ghost"
                  onClick={() => setModal("diff")}
                  disabled={rows.length < 2}
                >
                  <GitCompareArrows size={15} />
                  차이 보기
                </Button>
                <Button variant="ghost" onClick={download}>
                  <Download size={15} />
                  내려받기
                </Button>
                {canManage && !current.active && (
                  <Button
                    variant="secondary"
                    onClick={() => setModal("activate")}
                    disabled={!actions.activate}
                  >
                    활성으로
                  </Button>
                )}
              </div>
            </header>
            <RefineMarkdown body={current.body} />
          </section>
        </div>
      )}
      {canManage && (
        <Button
          className="mrf-fab"
          onClick={() => {
            setFailure("");
            setModal("create");
          }}
          disabled={!actions.create}
        >
          <Plus size={18} />새 버전 올리기
        </Button>
      )}
      {modal && (
        <Dialog
          open
          title={
            modal === "create"
              ? "새 가이드라인 버전"
              : modal === "activate"
                ? "활성 버전 전환"
                : "가이드라인 차이 보기"
          }
          size="wide"
          onOpenChange={(v) => !v && !busy && setModal(undefined)}
          footer={
            <DialogActions
              secondary={{
                label: modal === "diff" ? "닫기" : "취소",
                onClick: () => setModal(undefined),
                disabled: busy,
              }}
              primary={
                modal !== "diff"
                  ? {
                      label: busy
                        ? "저장 중…"
                        : modal === "create"
                          ? "새 버전 등록"
                          : "활성으로",
                      onClick: submit,
                      disabled: busy || !canManage,
                    }
                  : undefined
              }
            />
          }
        >
          <div className="mrf-form">
            {modal === "create" ? (
              <>
                <Field
                  label="버전 라벨"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
                <Field
                  label="본문 파일 (.md)"
                  type="file"
                  accept=".md"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!/\.md$/i.test(file.name)) {
                      setFailure(".md 파일을 선택해 주세요.");
                      return;
                    }
                    setBody(await file.text());
                  }}
                />
                <label className="mrf-field">
                  가이드라인 본문
                  <textarea
                    rows={12}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                  />
                </label>
                <Field
                  label="바꾼 이유"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <p>
                  새 버전은 비활성으로 등록합니다. 검토 후 활성으로 전환해
                  주세요.
                </p>
              </>
            ) : modal === "activate" ? (
              <p>
                이후 정형화는 v{current?.version}으로 돌아갑니다. 진행 중 시도는
                그대로 유지됩니다.
              </p>
            ) : (
              current &&
              compare && (
                <>
                  <Select
                    label="비교할 버전"
                    value={compare.id}
                    onChange={(e) => setOther(e.target.value)}
                  >
                    {rows
                      .filter((v) => v.id !== current.id)
                      .map((v) => (
                        <option value={v.id} key={v.id}>
                          v{v.version} · {v.label}
                        </option>
                      ))}
                  </Select>
                  <div className="mrf-comparison">
                    <section className="mrf-paper">
                      <header>이전 · v{compare.version}</header>
                      <pre className="mrf-diff">
                        {compare.body.split("\n").map((l, i) => (
                          <span
                            key={i}
                            className={
                              current.body.split("\n")[i] !== l
                                ? "mrf-removed"
                                : ""
                            }
                          >
                            {l || " "}
                            <br />
                          </span>
                        ))}
                      </pre>
                    </section>
                    <section className="mrf-paper">
                      <header>선택 · v{current.version}</header>
                      <pre className="mrf-diff">
                        {current.body.split("\n").map((l, i) => (
                          <span
                            key={i}
                            className={
                              compare.body.split("\n")[i] !== l
                                ? "mrf-added"
                                : ""
                            }
                          >
                            {l || " "}
                            <br />
                          </span>
                        ))}
                      </pre>
                    </section>
                  </div>
                </>
              )
            )}
            {failure && (
              <p className="mrf-error" role="alert">
                {failure}
              </p>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}
export function GuidelinePreview({
  example = "normal",
  canManage = true,
}: {
  example?: "normal" | "create" | "diff" | "readonly" | "empty" | "error";
  canManage?: boolean;
}) {
  const [versions, setVersions] = useState(
      example === "empty" ? [] : guidelineFixture,
    ),
    [error, setError] = useState(
      example === "error" ? "가이드라인을 불러오지 못했습니다." : undefined,
    );
  return (
    <GuidelineManager
      versions={versions}
      error={error}
      canManage={canManage && example !== "readonly"}
      initialDialog={
        example === "create" || example === "diff" ? example : undefined
      }
      actions={{
        reload: () => setError(undefined),
        create: async (input) => {
          const row = {
            ...input,
            id: crypto.randomUUID(),
            version:
              Math.max(
                0,
                ...versions
                  .filter((v) => v.kind === input.kind)
                  .map((v) => v.version),
              ) + 1,
            active: false,
            author: "운영 관리자",
            createdAt: "2026-10-22 11:00",
            uses: 0,
          };
          if (
            versions.some((v) => v.kind === input.kind && v.body === input.body)
          )
            throw Error("같은 내용의 가이드라인이 이미 있습니다.");
          setVersions((v) => [row, ...v]);
        },
        activate: async (id) =>
          setVersions((v) =>
            v.map((g) => ({
              ...g,
              active:
                g.kind === versions.find((x) => x.id === id)!.kind
                  ? g.id === id
                  : g.active,
            })),
          ),
      }}
    />
  );
}
